/**
 * G5 — Panneau admin « Localisation et qualité des coordonnées ».
 * Réservé SUPER_ADMIN / ADMIN (actions). Lecture via API qualité.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  MapPin,
  RefreshCw,
  AlertTriangle,
  Home,
  Navigation,
  Trash2,
  ShieldCheck,
} from 'lucide-react';
import apiService from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import {
  isCompleteCoordPair,
} from '../utils/coordinateQualityUi';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [46.603354, 1.888334];
const MAP_ZOOM = 15;

function MapRecenter({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.setView(target, map.getZoom());
  }, [target, map]);
  return null;
}

const TONE_CLASS = {
  success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-100 text-amber-900 border-amber-200',
  danger: 'bg-red-100 text-red-800 border-red-200',
  info: 'bg-sky-100 text-sky-900 border-sky-200',
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
};

function Badge({ label, tone = 'neutral' }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
        TONE_CLASS[tone] || TONE_CLASS.neutral
      }`}
    >
      {label}
    </span>
  );
}

function formatCoord(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(6);
}

function formatDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('fr-FR');
  } catch {
    return '—';
  }
}

const originalIcon = L.divIcon({
  className: '',
  html: '<div style="width:12px;height:12px;border-radius:9999px;background:#64748b;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,.35)"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

const overrideIcon = L.divIcon({
  className: '',
  html: '<div style="width:14px;height:14px;border-radius:9999px;background:#2563eb;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

/**
 * @param {{ commercantId: string, enabled?: boolean, onChanged?: () => void, toast?: { success: Function, error: Function, warning: Function } }} props
 */
export default function CoordinateQualityPanel({
  commercantId,
  enabled = true,
  onChanged,
  toast,
}) {
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');
  const [geocodeProposal, setGeocodeProposal] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!enabled || !commercantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getCommercantCoordinateQuality(commercantId);
      const q = res?.quality || res;
      setQuality(q);
      if (q?.override?.latitude != null) {
        setLatInput(String(q.override.latitude));
        setLngInput(String(q.override.longitude));
      } else if (q?.effective?.latitude != null) {
        setLatInput(String(q.effective.latitude));
        setLngInput(String(q.effective.longitude));
      } else {
        setLatInput('');
        setLngInput('');
      }
    } catch (err) {
      const msg =
        err?.error?.message || err?.message || 'Impossible de charger la qualité des coordonnées.';
      setError(msg);
      setQuality(null);
    } finally {
      setLoading(false);
    }
  }, [commercantId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const mapCenter = useMemo(() => {
    if (quality?.effective?.latitude != null && quality?.effective?.longitude != null) {
      return [quality.effective.latitude, quality.effective.longitude];
    }
    if (quality?.original?.latitude != null && quality?.original?.longitude != null) {
      return [quality.original.latitude, quality.original.longitude];
    }
    return DEFAULT_CENTER;
  }, [quality]);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      const q = res?.quality || res;
      if (q) setQuality(q);
      toast?.success?.(successMsg);
      onChanged?.();
      await load();
    } catch (err) {
      const msg = err?.error?.message || err?.message || 'Action impossible.';
      setError(msg);
      toast?.error?.(msg);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const validatePairInputs = () => {
    if (!isCompleteCoordPair(latInput, lngInput)) {
      toast?.warning?.(
        'Paire latitude/longitude complète requise (−90…90 / −180…180, pas (0,0)).'
      );
      return null;
    }
    return { latitude: Number(latInput), longitude: Number(lngInput) };
  };

  if (!enabled) return null;

  return (
    <div
      id="section-localisation-qualite"
      className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-slate-600" />
            Localisation et qualité des coordonnées
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Outil administrateur : visualiser, corriger et valider la position utilisée pour la proximité.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || busy}
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && !quality && (
        <p className="mt-4 text-sm text-slate-500">Chargement…</p>
      )}

      {quality && (
        <div className="mt-4 space-y-5">
          <div className="flex flex-wrap gap-2">
            {(quality.badges || []).map((b) => (
              <Badge key={`${b.key}-${b.label}`} label={b.label} tone={b.tone} />
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">Adresse</p>
              <p className="mt-1 text-slate-900">
                {[quality.address?.rue, quality.address?.codePostal, quality.address?.ville]
                  .filter(Boolean)
                  .join(', ') || 'Non renseignée'}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">Position utilisée</p>
              <p className="mt-1 font-semibold text-slate-900">
                {quality.effective?.kindLabel} — {formatCoord(quality.effective?.latitude)} /{' '}
                {formatCoord(quality.effective?.longitude)}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">Statut</p>
              <p className="mt-1 font-semibold text-slate-900">{quality.status?.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Source : {quality.quality?.sourceLabel} · Précision :{' '}
                {quality.quality?.precisionLabel} · Fraîcheur : {quality.quality?.freshnessLabel}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">Originales</p>
              <p className="mt-1">
                {formatCoord(quality.original?.latitude)} / {formatCoord(quality.original?.longitude)}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">Correction manuelle</p>
              <p className="mt-1">
                {formatCoord(quality.override?.latitude)} / {formatCoord(quality.override?.longitude)}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">Traçabilité</p>
              <p className="mt-1 text-xs text-slate-700 space-y-0.5">
                <span className="block">Validée : {formatDate(quality.timestamps?.coordinatesValidatedAt)}</span>
                <span className="block">Géocodée : {formatDate(quality.timestamps?.coordinatesGeocodedAt)}</span>
                <span className="block">Modifiée : {formatDate(quality.timestamps?.coordinatesUpdatedAt)}</span>
              </p>
            </div>
          </div>

          {quality.fingerprint?.matches === false && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              L’adresse a changé depuis le géocodage ou la dernière validation. Revalidez la position si elle est
              toujours correcte.
            </div>
          )}

          <div className="h-64 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            {quality.effective?.latitude != null || quality.original?.latitude != null ? (
              <MapContainer
                center={mapCenter}
                zoom={MAP_ZOOM}
                scrollWheelZoom
                className="h-full w-full"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapRecenter target={mapCenter} />
                {quality.original?.latitude != null && quality.original?.longitude != null && (
                  <Marker
                    position={[quality.original.latitude, quality.original.longitude]}
                    icon={originalIcon}
                  />
                )}
                {quality.override?.latitude != null && quality.override?.longitude != null && (
                  <Marker
                    position={[quality.override.latitude, quality.override.longitude]}
                    icon={overrideIcon}
                  />
                )}
                {quality.quality?.usableForDistance === false &&
                  quality.effective?.latitude != null && (
                    <CircleMarker
                      center={[quality.effective.latitude, quality.effective.longitude]}
                      radius={10}
                      pathOptions={{ color: '#dc2626', fillColor: '#fecaca', fillOpacity: 0.5 }}
                    />
                  )}
              </MapContainer>
            ) : (
              <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
                Aucune coordonnée à afficher. Saisissez une paire ou géocodez l’adresse.
              </div>
            )}
          </div>
          {!quality.quality?.usableForDistance && quality.effective?.latitude != null && (
            <p className="text-xs text-red-700">
              Alerte : la paire effective n’est pas utilisable pour le calcul de distance.
            </p>
          )}

          <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 space-y-3">
            <p className="text-sm font-semibold text-blue-900">Correction manuelle (override)</p>
            <p className="text-xs text-blue-800/80">
              Paire complète obligatoire. Aucune combinaison partielle avec l’historique. (0,0) refusé.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                value={latInput}
                onChange={(e) => setLatInput(e.target.value)}
                placeholder="Latitude"
                className="rounded-md border border-blue-200 px-3 py-2 text-sm"
              />
              <input
                type="text"
                value={lngInput}
                onChange={(e) => setLngInput(e.target.value)}
                placeholder="Longitude"
                className="rounded-md border border-blue-200 px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  const pair = validatePairInputs();
                  if (!pair) return;
                  setConfirm({
                    title: 'Enregistrer la correction manuelle ?',
                    description:
                      'La position effective utilisera cet override. Elle ne sera pas validée automatiquement.',
                    onConfirm: () =>
                      runAction(
                        () => apiService.setCommercantCoordinateOverride(commercantId, pair),
                        'Correction manuelle enregistrée'
                      ),
                  });
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Enregistrer l’override
              </button>
              {quality.actions?.canClearOverride && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    setConfirm({
                      title: 'Supprimer la correction manuelle ?',
                      description:
                        'Retour aux coordonnées originales. La validation de l’override ne sera pas transférée.',
                      tone: 'danger',
                      onConfirm: () =>
                        runAction(
                          () => apiService.clearCommercantCoordinateOverride(commercantId),
                          'Correction manuelle supprimée'
                        ),
                    })
                  }
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Supprimer la correction manuelle
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !quality.actions?.canValidate}
              onClick={() =>
                setConfirm({
                  title: 'Valider cette position ?',
                  description:
                    'La date de validation sera fixée par le serveur. Précision EXACT demandée. Confirmation requise.',
                  onConfirm: () =>
                    runAction(
                      () =>
                        apiService.validateCommercantCoordinates(commercantId, {
                          precision: 'EXACT',
                        }),
                      'Position validée'
                    ),
                })
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Valider cette position
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                setConfirm({
                  title: 'Marquer comme à vérifier ?',
                  description:
                    'Les coordonnées sont conservées. La validation précédente est retirée.',
                  tone: 'danger',
                  onConfirm: () =>
                    runAction(
                      () => apiService.markCommercantCoordinatesReview(commercantId),
                      'Position marquée à vérifier'
                    ),
                })
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Marquer comme à vérifier
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                if (!quality.address?.rue || !quality.address?.codePostal || !quality.address?.ville) {
                  toast?.warning?.('Adresse incomplète pour le géocodage.');
                  return;
                }
                setBusy(true);
                try {
                  const result = await apiService.geocodeAdresse({
                    adresseLigne1: quality.address.rue,
                    adresseLigne2: quality.address.adresse2,
                    codePostal: quality.address.codePostal,
                    ville: quality.address.ville,
                    pays: quality.address.pays || 'France',
                  });
                  setGeocodeProposal(result);
                  toast?.success?.('Proposition trouvée — confirmez pour l’appliquer.');
                } catch (err) {
                  const msg =
                    err?.error?.message || err?.message || 'Géocodage impossible.';
                  toast?.error?.(msg);
                } finally {
                  setBusy(false);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Navigation className="h-3.5 w-3.5" />
              Rechercher les coordonnées depuis l’adresse
            </button>
          </div>

          {geocodeProposal && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="font-medium text-slate-800">Proposition géocodée (non validée)</p>
              <p className="mt-1 text-slate-600">
                {geocodeProposal.formatted || '—'} — {formatCoord(geocodeProposal.latitude)} /{' '}
                {formatCoord(geocodeProposal.longitude)}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    setConfirm({
                      title: 'Appliquer le géocodage ?',
                      description:
                        'Les coordonnées originales seront mises à jour (source GEOCODED). Pas de validation automatique.',
                      onConfirm: () =>
                        runAction(async () => {
                          const res = await apiService.applyCommercantGeocode(commercantId, {
                            latitude: geocodeProposal.latitude,
                            longitude: geocodeProposal.longitude,
                          });
                          setGeocodeProposal(null);
                          return res;
                        }, 'Géocodage appliqué'),
                    })
                  }
                  className="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Confirmer la proposition
                </button>
                <button
                  type="button"
                  onClick={() => setGeocodeProposal(null)}
                  className="rounded-md border px-3 py-1.5 text-xs font-semibold text-slate-600"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-violet-950 flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Commerce à domicile
                </p>
                <p className="mt-1 text-xs text-violet-900/80">
                  Les coordonnées exactes de ce commerce ne seront pas exposées publiquement. Elles peuvent
                  uniquement servir au calcul interne de distance.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-violet-950">
                <input
                  type="checkbox"
                  checked={Boolean(quality.commerceADomicile)}
                  disabled={busy}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setConfirm({
                      title: next
                        ? 'Activer le mode domicile ?'
                        : 'Désactiver le mode domicile ?',
                      description: next
                        ? 'Les coordonnées précises seront masquées sur les réponses publiques.'
                        : 'Les coordonnées pourront à nouveau apparaître sur les réponses publiques.',
                      onConfirm: () =>
                        runAction(
                          () =>
                            apiService.setCommercantCommerceADomicile(commercantId, next),
                          'Statut domicile mis à jour'
                        ),
                    });
                  }}
                  className="h-4 w-4 rounded border-violet-300 text-violet-700"
                />
                Actif
              </label>
            </div>
          </div>

          {quality.magasinItinerant && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-800">Créneaux itinérants — localisation</p>
              {(quality.itinerantSlots || []).length === 0 && (
                <p className="text-xs text-slate-500">Aucun créneau avec localisation.</p>
              )}
              {(quality.itinerantSlots || []).map((slot) => (
                <div
                  key={slot.id}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold">
                      Jour {slot.jour} · {slot.heureDebut}–{slot.heureFin}
                      {slot.libelleLieu ? ` · ${slot.libelleLieu}` : ''}
                    </span>
                    <Badge label={slot.statusLabel} tone={slot.quality?.usableForDistance ? 'neutral' : 'danger'} />
                  </div>
                  <p className="mt-1">
                    {[slot.adresseLigne1, slot.codePostal, slot.ville].filter(Boolean).join(', ') ||
                      'Adresse créneau absente'}
                  </p>
                  <p className="mt-0.5">
                    Coords : {formatCoord(slot.latitude)} / {formatCoord(slot.longitude)} · Source{' '}
                    {slot.coordinatesSource || '—'} · Précision {slot.coordinatesPrecision || '—'}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy || slot.latitude == null}
                      className="rounded border border-red-200 px-2 py-1 text-red-700 hover:bg-red-50 disabled:opacity-40"
                      onClick={() =>
                        setConfirm({
                          title: 'Supprimer les coords du créneau ?',
                          description: 'Le siège du commerce ne sera jamais utilisé en secours.',
                          tone: 'danger',
                          onConfirm: () =>
                            runAction(
                              () =>
                                apiService.updateItinerantSlotCoordinates(
                                  commercantId,
                                  slot.id,
                                  { clear: true }
                                ),
                              'Coords créneau effacées'
                            ),
                        })
                      }
                    >
                      Effacer la paire
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        title={confirm?.title}
        description={confirm?.description}
        tone={confirm?.tone || 'primary'}
        confirmDisabled={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.onConfirm?.()}
        confirmLabel="Confirmer"
      />
    </div>
  );
}
